import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

const AuthContext = createContext(null);

const AUTH_SESSION_EXPIRED_REQUEST = "mbt-session-expired-request";
const AUTH_SESSION_EXPIRED_EVENT = "mbt-session-expired";

const TOKEN_KEY = "mbt_token";
const USER_KEY = "mbt_user";

export function requestSessionExpiry() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(AUTH_SESSION_EXPIRED_REQUEST)
    );
  }
}

function getUserId(value) {
  if (!value) return null;

  const rawId =
    value.id ??
    value.userId ??
    value.user_id ??
    value._id ??
    null;

  if (rawId == null) return null;

  const id = String(rawId).trim();

  if (
    !id ||
    id.toLowerCase() === "null" ||
    id.toLowerCase() === "undefined"
  ) {
    return null;
  }

  return id;
}

// Decode JWT payload for client-side session display only.
// The backend remains responsible for validating the token.
function getTokenPayload(tokenValue) {
  try {
    if (typeof tokenValue !== "string") return null;

    const parts = tokenValue.split(".");
    if (parts.length !== 3) return null;

    const base64 = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    return JSON.parse(atob(paddedBase64));
  } catch {
    return null;
  }
}

function isTokenExpired(tokenValue) {
  const payload = getTokenPayload(tokenValue);
  const expiry = Number(payload?.exp);

  return (
    !payload ||
    !Number.isFinite(expiry) ||
    expiry * 1000 <= Date.now()
  );
}

function getApiBase() {
  return (
    process.env.REACT_APP_API_URL || "https://makebeetrip.com"
  ).replace(/\/+$/, "");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Restore the saved session when the application starts.
  useEffect(() => {
    try {
      const savedToken = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);

      if (!savedToken || !savedUser || isTokenExpired(savedToken)) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return;
      }

      const parsedUser = JSON.parse(savedUser);

      if (!parsedUser || typeof parsedUser !== "object") {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return;
      }

      const tokenPayload = getTokenPayload(savedToken);
      const userId = getUserId(parsedUser) || getUserId(tokenPayload);

      if (!userId) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return;
      }

      const restoredUser = {
        ...parsedUser,
        id: userId,
      };

      localStorage.setItem(USER_KEY, JSON.stringify(restoredUser));

      setToken(savedToken);
      setUser(restoredUser);
      setIsLoggedIn(true);
    } catch (error) {
      console.error("Unable to restore authentication session:", error);

      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  // Call this only after the backend successfully authenticates the user.
  // For WhatsApp OTP, pass the token and user returned by verify-otp.
  const login = useCallback((userObject, tokenValue) => {
    if (!tokenValue || isTokenExpired(tokenValue)) {
      throw new Error("A valid authentication token is required.");
    }

    if (!userObject || typeof userObject !== "object") {
      throw new Error("A valid authenticated user is required.");
    }

    const tokenPayload = getTokenPayload(tokenValue);
    const userId = getUserId(userObject) || getUserId(tokenPayload);

    if (!userId) {
      throw new Error("The authenticated user ID is missing.");
    }

    const authenticatedUser = {
      ...userObject,
      id: userId,
    };

    localStorage.setItem(TOKEN_KEY, tokenValue);
    localStorage.setItem(USER_KEY, JSON.stringify(authenticatedUser));

    setToken(tokenValue);
    setUser(authenticatedUser);
    setIsLoggedIn(true);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
  }, []);

  const expireSession = useCallback(() => {
    logout();

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(AUTH_SESSION_EXPIRED_EVENT)
      );
    }
  }, [logout]);

  useEffect(() => {
    const handleExpiryRequest = () => {
      expireSession();
    };

    window.addEventListener(
      AUTH_SESSION_EXPIRED_REQUEST,
      handleExpiryRequest
    );

    return () => {
      window.removeEventListener(
        AUTH_SESSION_EXPIRED_REQUEST,
        handleExpiryRequest
      );
    };
  }, [expireSession]);

  // Retrieve the current account from the backend.
  const fetchCurrentUser = useCallback(
    async (tokenValue = token) => {
      if (!tokenValue) {
        throw new Error("Authentication token is missing.");
      }

      if (isTokenExpired(tokenValue)) {
        if (tokenValue === token) {
          expireSession();
        }

        throw new Error("Your session has expired. Please log in again.");
      }

      const response = await fetch(`${getApiBase()}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${tokenValue}`,
        },
      });

      if (response.status === 401) {
        if (tokenValue === token) {
          expireSession();
        }

        throw new Error("Your session is invalid. Please log in again.");
      }

      if (!response.ok) {
        throw new Error("Could not load the authenticated user.");
      }

      const data = await response.json();

      if (!data?.user) {
        throw new Error("The server did not return the authenticated user.");
      }

      return data.user;
    },
    [token, expireSession]
  );

  // Update local profile state after a successful profile API response.
  const updateUser = useCallback((partialUpdate) => {
    setUser((previousUser) => {
      if (!previousUser) return previousUser;

      const updatedUser = {
        ...previousUser,
        ...partialUpdate,
      };

      try {
        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      } catch (error) {
        console.error("Failed to update saved user:", error);
      }

      return updatedUser;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        authUser: user,
        token,
        isLoggedIn,
        authLoading,
        login,
        fetchCurrentUser,
        logout,
        expireSession,
        updateUser,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}